import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Clock3, User, Calendar } from "lucide-react";
import { fetchPost } from "@/lib/api";
import SEO, { blogPostSchema } from "@/components/SEO";

function formatDate(iso) {
    if (!iso) return "";
    try {
        const d = typeof iso === "string" ? new Date(iso) : iso;
        return d.toLocaleDateString("it-CH", { year: "numeric", month: "long", day: "numeric" });
    } catch {
        return "";
    }
}

export default function BlogPost() {
    const { slug } = useParams();
    const [state, setState] = useState({ loading: true, post: null, error: null });

    useEffect(() => {
        setState({ loading: true, post: null, error: null });
        fetchPost(slug)
            .then((post) => setState({ loading: false, post, error: null }))
            .catch((e) => setState({ loading: false, post: null, error: e.response?.status === 404 ? "not_found" : e.message }));
    }, [slug]);

    if (state.loading) {
        return (
            <div className="container-7m py-32 text-center">
                <div className="w-2 h-2 bg-brand rounded-full animate-pulse-dot mx-auto"></div>
                <div className="mt-4 text-xs font-mono uppercase tracking-[0.2em] text-ink-muted">Caricamento…</div>
            </div>
        );
    }
    if (state.error === "not_found") {
        return (
            <div className="container-7m py-32 text-center">
                <div className="font-heading font-black text-6xl text-brand">404</div>
                <h1 className="mt-4 font-heading font-black text-3xl">Articolo non trovato</h1>
                <Link to="/blog" className="btn-primary mt-8">Tutti gli articoli</Link>
            </div>
        );
    }
    const p = state.post;

    return (
        <article>
            <SEO
                title={p.title}
                description={p.summary}
                path={`/blog/${p.slug}`}
                ogImage={p.cover_image}
                type="article"
                keywords={(p.tags || []).join(", ")}
                structuredData={blogPostSchema(p)}
                breadcrumbs={[
                    { name: "Home", path: "/" },
                    { name: "Blog", path: "/blog" },
                    { name: p.title, path: `/blog/${p.slug}` },
                ]}
            />
            {p.cover_image && (
                <section className="bg-ink">
                    <div className="aspect-[21/9] w-full max-h-[70vh] overflow-hidden">
                        <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover opacity-80" />
                    </div>
                </section>
            )}

            <section className="py-16 lg:py-24 bg-white">
                <div className="container-7m max-w-3xl">
                    <Link to="/blog" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-ink-muted hover:text-brand">
                        <ArrowLeft className="w-3.5 h-3.5" /> Tutti gli articoli
                    </Link>

                    <div className="mt-8 label-eyebrow text-brand">{p.category}</div>
                    <h1 className="mt-4 font-heading font-black text-4xl lg:text-6xl tracking-tighter leading-[1.02]">{p.title}</h1>

                    <div className="mt-8 flex flex-wrap items-center gap-5 text-xs font-mono uppercase tracking-wider text-ink-muted">
                        <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{p.author}</span>
                        <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{formatDate(p.published_at || p.created_at)}</span>
                        <span className="flex items-center gap-1.5"><Clock3 className="w-3.5 h-3.5" />{p.reading_minutes} min di lettura</span>
                    </div>

                    <div className="mt-6 text-xl text-ink-muted leading-relaxed border-l-2 border-brand pl-6 py-2">
                        {p.summary}
                    </div>

                    <div className="mt-12 prose-7m">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{p.content}</ReactMarkdown>
                    </div>

                    {p.tags && p.tags.length > 0 && (
                        <div className="mt-12 pt-8 border-t border-ink/10 flex flex-wrap gap-2">
                            {p.tags.map((t) => (<span key={t} className="px-3 py-1 border border-ink/20 text-xs font-mono uppercase tracking-wider">#{t}</span>))}
                        </div>
                    )}
                </div>
                <style>{`
                    .prose-7m { font-size: 17px; line-height: 1.8; color: #1f2937; }
                    .prose-7m h1 { font-family: "Cabinet Grotesk", sans-serif; font-weight: 900; font-size: 40px; line-height: 1.1; letter-spacing: -0.03em; margin: 56px 0 20px; color: #0a0a0a; }
                    .prose-7m h2 { font-family: "Cabinet Grotesk", sans-serif; font-weight: 800; font-size: 30px; line-height: 1.2; letter-spacing: -0.02em; margin: 48px 0 16px; color: #0a0a0a; }
                    .prose-7m h3 { font-family: "Cabinet Grotesk", sans-serif; font-weight: 700; font-size: 22px; margin: 32px 0 12px; color: #0a0a0a; }
                    .prose-7m p { margin: 0 0 20px; }
                    .prose-7m a { color: #FF3B30; text-decoration: underline; text-underline-offset: 3px; }
                    .prose-7m a:hover { color: #D62828; }
                    .prose-7m strong { color: #0a0a0a; font-weight: 600; }
                    .prose-7m em { font-style: italic; }
                    .prose-7m ul, .prose-7m ol { margin: 16px 0 24px 24px; }
                    .prose-7m ul { list-style: square; }
                    .prose-7m ol { list-style: decimal; }
                    .prose-7m li { margin-bottom: 8px; }
                    .prose-7m blockquote { margin: 32px 0; padding: 16px 24px; border-left: 3px solid #FF3B30; background: #FAFAFA; font-style: italic; color: #374151; }
                    .prose-7m code { background: #f4f4f5; padding: 2px 6px; font-size: 0.9em; border-radius: 2px; font-family: "IBM Plex Mono", monospace; }
                    .prose-7m pre { background: #0a0a0a; color: #fafafa; padding: 20px; overflow-x: auto; margin: 24px 0; border-radius: 2px; font-family: "IBM Plex Mono", monospace; font-size: 14px; }
                    .prose-7m pre code { background: transparent; padding: 0; color: inherit; }
                    .prose-7m hr { border: 0; border-top: 1px solid #e5e7eb; margin: 48px 0; }
                    .prose-7m img { margin: 24px 0; max-width: 100%; height: auto; }
                    .prose-7m table { width: 100%; border-collapse: collapse; margin: 24px 0; font-size: 15px; }
                    .prose-7m th, .prose-7m td { border: 1px solid #e5e7eb; padding: 10px; text-align: left; }
                    .prose-7m th { background: #fafafa; font-weight: 700; }
                `}</style>
            </section>

            <section className="py-20 bg-[#FAFAFA] border-t border-ink/10">
                <div className="container-7m text-center">
                    <Link to="/blog" className="btn-secondary">← Tutti gli articoli</Link>
                </div>
            </section>
        </article>
    );
}
